import { useState, useMemo, useCallback, useRef, useEffect, useId } from 'react'
import { flushSync } from 'react-dom'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { unparse } from 'papaparse'
import LoadingSpinner from '@components/common/LoadingSpinner'
import ColumnValuePicker from '@components/common/ColumnValuePicker'
import ControlCheckbox from '@components/common/ControlCheckbox'
import { HANDLE_BOX_PX4, DIVIDER_BASE, DIVIDER_RESTING } from '@components/common/gridHeaderChrome'
import PopoverMenu from '@components/common/PopoverMenu'
import { lastVisibleColumnId, recoverEveryColumnHidden } from '@components/common/columnVisibilityGuard'
import { SlidersIcon, FunnelIcon } from '@components/reports/queue/queueIcons'
// D3 (2026-08-27): the header info-icon gate, shared with ReportGroupedGrid so the
// 33 flat reports and the 86 grouped ones cannot drift. A plain leaf module, not a
// helper exported from either grid — see the rationale on the predicate.
import { isAuthoredDefinition } from '@components/reports/columnDefinitionGate'
import usePreferencesStore from '@store/preferencesStore'
import { PENDO_IGNORE } from '@/analytics/domPrivacy'
import { trackGridTransform } from '@/analytics/gridTransforms'
import {
  PIN_CELL_CLASS,
  pinFlagName,
  pinFlagsToClearOnUnfreeze,
  pinToggleTargetId,
  resolvePinnedColumnId,
  orderWithPinFirst,
  usePinnedScrollShadow,
} from '@utils/columnPinning'

// Column alignment (CC-49735): column defs carry a resolved `meta.align`
// ('left' | 'center' | 'right'); cells/footer get a text-align class and the
// header content mirrors it with flex justification. Static class maps keep
// Tailwind's scanner happy (no dynamic class names).
const ALIGN_TEXT = { left: 'text-left', center: 'text-center', right: 'text-right' }
const ALIGN_JUSTIFY = { left: '', center: 'justify-center', right: 'justify-end' }

/*
 * B2 (Himaad + Jordan, 2026-09-03, spec 116) — the reorder handle's TARGET, sized
 * once and shared with the frozen-column marker so the two keep the same footprint.
 *
 * Jordan, verbatim: "we can leave the dots, but lets make the grabbable space much
 * larger like we did with the divider bar/resizing." Himaad had suggested hiding the
 * ⠿ until hover; Jordan overruled that, so the GLYPH IS UNCHANGED — still ⠿, still
 * text-[10px], still always visible. Only the box around it grew.
 *
 * Before: the handle's target was the text box of a 10px glyph — MEASURED in Chrome
 * at 7.11 x 10.00 CSS px, not the "about 10 x 12" this comment estimated in round 2.
 * SC 2.5.8's floor is 24 x 24. This is `h-6 w-6` = 24 x 24 with the glyph centred,
 * which is the same decouple-the-target-from-the-visual move the resize strip makes
 * further down.
 *
 * Why the negative margins, and why THOSE numbers — the 24px has to be real, and two
 * neighbours can steal it:
 *
 *   -my-1  The box is 24px tall but contributes 24 - 8 = 16px to the flex line, which
 *          is exactly the header row's existing content height (text-xs = 12px on a
 *          16px line box). So no grid's header gets taller. The 4px it overhangs top
 *          and bottom lands inside the th's own py-3 (12px), uncontested.
 *
 *   -ml-[3px]  The PREVIOUS column's resize strip overhangs 12px into this th and
 *          carries z-10, so measuring from the header's content box (x=0 at the th's
 *          px-4 edge) that strip owns x = -16..-3. -3px puts this box's left edge
 *          exactly where the strip stops. One pixel further left and the leftmost
 *          column of the target would be dead — which is the mistake the resize strip
 *          itself made in round 1 ("0px on the right", see its comment): a target that
 *          measures 24px and behaves like 20px.
 *
 *   -mr-1  Reclaims the 4px the box would otherwise push the label across by, so the
 *          sort button starts at x = 21 — flush with the box's right edge, not over it.
 *          The sort button is a real control later in DOM order, so any overlap would
 *          be won by IT, not by the handle; and giving the handle a z-index to win
 *          instead would make the first few pixels of every column label un-sortable.
 *
 * flex-none  ⚠️ ADDED 2026-09-08 (spec 116 round 3) — WITHOUT IT THE 24px WAS
 *          CONDITIONAL, and the condition is routine rather than exotic. This box is a
 *          flex item of the header's `flex items-center gap-1` row inside a
 *          `table-fixed` th, so its default `flex: 0 1 auto` + `min-width: auto`
 *          resolves the automatic minimum to min(24px, the ⠿ glyph's ~10px
 *          min-content) — i.e. the box SHRINKS back toward the pre-change size on any
 *          column whose header content is wider than the column. Measured in real
 *          Chrome against this exact class string (Tailwind v4 preflight, table-fixed,
 *          the same flex row): 150px column / header "Date" → 24.00 x 24.00; 110px
 *          column / header "Total Billable Hours This Period" → 10.08 x 24.00; 60px
 *          column → 7.11 x 24.00. Setting flex-none returned all three to 24.00 x
 *          24.00 in the same session. Narrow columns are not a corner case here: the
 *          keyboard resize handler floors at 40px on purpose (see the strip below), so
 *          "the user narrowed a column" is a first-class, supported state, and the ONE
 *          surface Jordan asked for ("make the grabbable space much larger") was
 *          silently losing the fix there. GridCheckbox's own wrapper below already
 *          carries flex-none for the same reason; this was an omission, not a choice.
 *          The compression moves onto the label wrapper instead, which is the right
 *          trade: a label can spill or truncate, a hit target cannot.
 *
 * Net: an uncontested 24 x 24 target — at every column width, not only wide ones —
 * header row height unchanged, the glyph shifted 4px right and the label right by
 * roughly 24px minus the glyph's advance width (measured ~9.9px in Chrome/system-ui,
 * so ~14px; font-dependent, which is why no exact figure is claimed).
 *
 * jsdom applies no CSS, so none of the above is measurable with
 * getBoundingClientRect in a unit test — the emitted geometry is asserted by class
 * instead, in TWO places with different jobs. gridChromeDividerHandle.test.jsx
 * covers the FLAT grid only, so it cannot stand in for cross-renderer parity;
 * headerAffordances.grids.test.jsx runs describe.each over BOTH renderers and, as of
 * spec 121, is the guard that fails when one grid's handle box or divider drifts from
 * the other's. (An earlier version of this comment said that file "contains no
 * HANDLE_BOX assertion at all" — true when written, and the reason the 86 grouped
 * reports silently kept a ~7px target for a round; spec 121 added the assertions.)
 * A class-only suite cannot tell 24px from
 * 7px, which is exactly how the missing flex-none passed round 2 green; the anchored
 * `flex-none` assertion with a negative twin is the closest a unit test gets, and the
 * pixels are gated post-deploy by e2e/reports-grid-audit.spec.js at a viewport narrow
 * enough to force overflow.
 *
 * ⚠️ MOVED 2026-09-09 (spec 121). The string now lives in
 * `src/components/common/gridHeaderChrome.js` and is imported by BOTH grids, because
 * for six days this constant existed only here and the 86 grouped reports kept the
 * ~7-10px target it was written to replace. The `-ml-[3px]` is the half that is NOT
 * shared — it is arithmetic for a `px-4` header cell and buries 3px of the target
 * under a live resize strip in the grouped grid's `px-3` one — so the module exports
 * `HANDLE_BOX_PX4` (this file) and `HANDLE_BOX_PX3` separately. The subtraction is
 * written out there; do not copy either one to a third grid without redoing it.
 */
const HANDLE_BOX = HANDLE_BOX_PX4

/**
 * ⚠️ `GridCheckbox` WAS HERE, AND IT IS GONE (deleted 2026-09-09, spec 121 gap 3).
 *
 * spec 116 added it to this file on 2026-09-03 - a 14px drawn box with a real 1px
 * border, replacing this file's three native checkboxes whose
 * `rounded border-<colour> text-cb-accent-text focus:ring-cb-accent` painted nothing
 * (Tailwind v4 preflight sets `border-width: 0` on `*`, so a border colour with no
 * bare `border` width has no border to colour; a ring colour with no ring width
 * emits nothing; and `text-*` does not reach a native checkbox at all).
 *
 * All of that reasoning still holds and still ships - but it shipped TWICE. spec 117
 * had landed `src/components/common/ControlCheckbox.jsx` a week earlier, from the
 * same Himaad finding, and the two produced a byte-identical box and tick: same 14px
 * grid, same 3px radius, same bare `border`, same `border-rc-seam bg-white`
 * unchecked / `border-rc-chip-text bg-rc-chip-text text-white` checked pair, same
 * inline SVG tick (viewBox 0 0 10 8, h-2 w-2.5, strokeWidth 2.2). Two components
 * cannot be kept in step by intention; the divider one file over proves it.
 *
 * ControlCheckbox is the survivor, and the deciding reason is an import CYCLE, not
 * taste: this file imports ColumnValuePicker, which imports ControlCheckbox, so
 * migrating the other way would have made DataGrid -> ColumnValuePicker -> DataGrid.
 * Four of the seven consumer files do not import DataGrid at all.
 *
 * What moved with it: the 24x24 hit overlay, now the opt-in `hit24` prop, needed at
 * the two row-selection sites because they are the only ones with no wrapping
 * <label> to inherit a target from - a bare swap there would have been a silent
 * 24 -> 14 SC 2.5.8 regression, invisible because the drawn box looks identical at
 * both hit sizes. What the migration GAINS at those two sites is `print:hidden`,
 * which restores the pre-spec-116 paper output exactly (see ControlCheckbox's
 * docblock: a CHECKED GridCheckbox printed as an EMPTY box). The contrast argument -
 * why the unchecked border is --color-rc-seam #6b7280 and not --color-rc-control
 * #8b95a5, measured against every fill it renders on rather than only white - lives
 * there in full and is unchanged.
 *
 * Do not re-add a second checkbox component to this file.
 */

function DraggableHeader({ header, children, ariaSortValue, normalCase = false, isPinned = false, isLastHeader = false }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    // CC-49462: a frozen column is hoisted to the left edge and taken out of the
    // reorder set — dragging it (or dropping onto it) would move it away from
    // the edge it is pinned to. It stays resizable (CC-49380) and keeps its
    // alignment (CC-49735); only reordering is disabled.
  } = useSortable({ id: header.id, disabled: isPinned })

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    width: header.getSize(),
  }

  const headerLabel = typeof header.column.columnDef.header === 'string'
    ? header.column.columnDef.header
    : header.column.id

  return (
    <th
      ref={setNodeRef}
      style={style}
      scope="col"
      // CC-50173: name the header cell EXPLICITLY.
      //
      // Not belt-and-braces — the computed name was wrong. A `th` is named by its
      // contents, and this one contains the ⠿ drag handle and (when the column has
      // one) the "About {col}: {long definition}" info icon, so the cell announced
      // as roughly "⠿ Date About Date: the date the entry was worked" instead of
      // "Date". aria-label pins it to the column's own label; the decorations stay
      // reachable in their own right (see the handle's own label below).
      aria-label={headerLabel}
      aria-sort={ariaSortValue}
      className={`relative px-4 py-3 text-left font-semibold select-none ${normalCase ? 'text-[13px] text-gray-700' : 'text-xs text-gray-500 uppercase tracking-wider'} ${isPinned ? PIN_CELL_CLASS : ''}`}
    >
      {/* Header text follows the column's data alignment (CC-49735). */}
      <div className={`flex items-center gap-1 ${ALIGN_JUSTIFY[header.column.columnDef.meta?.align] || ''}`}>
        {isPinned ? (
          <span
            role="img"
            aria-label={`${headerLabel} is frozen and stays visible when you scroll right`}
            title="Frozen column — stays visible when you scroll right"
            /* HANDLE_BOX only so the frozen column's label lines up with every
               other column's — this marker is a role="img", not a control, and
               owes SC 2.5.8 nothing. Without it the pinned header's text would sit
               ~14px left of its neighbours' after B2 grew the reorder target.

               ✅ FIXED 2026-09-09 (spec 121 gap 4). It was `text-gray-400`, and
               spec 116 recorded that as "#9ca3af, 2.451:1 on the thead's #fafbfc".
               BOTH of those figures were wrong, in the same two ways, and neither
               error changed the verdict:
                 - the HEX is stale. Tailwind 4.2.1 ships an oklch palette, so
                   gray-400 is oklch(70.7% .022 261.325) = #99a1af, not v3's #9ca3af
                   (node_modules/tailwindcss/theme.css is the authority);
                 - the BACKDROP is wrong. src/index.css's unlayered
                   `thead .cb-pin-cell { background-color: var(--color-gray-50) }`
                   paints this cell OPAQUE #f9fafb on every grid, sticky header or
                   not — the /80 #fafbfc composite never applies to a pinned header
                   cell.
               Measured first-party: #99a1af on #f9fafb is 2.490:1, under SC 1.4.11's
               3.0:1 floor, which this ▌ owes because it is a graphical object
               conveying the column's frozen STATE.
               Now --color-rc-muted #6b7280 = 4.626:1 on #f9fafb. That is the
               MINIMUM-compliant colour available as an existing token: no token in
               @theme and no neutral step in the Tailwind palette lands between
               2.896:1 (--color-rc-control, fails) and 4.626:1, so a bespoke #8a929f
               (3.002:1) would have been a new token. Declined — it would also split
               this mark from the sort mark, which is already text-rc-muted for the
               identical reason (sole indicator of a state). See spec 121 §4. */
            className={`${HANDLE_BOX} text-rc-muted text-[10px] leading-none`}
          >
            {/* aria-hidden so the glyph does not read out after the label the
                role="img" already supplies (CC-50173). */}
            <span aria-hidden="true">▌</span>
          </span>
        ) : (
          <span
            {...attributes}
            {...listeners}
            // useSortable's `attributes` make this a focusable role="button", so it
            // WAS a keyboard-reachable control whose entire accessible name was a
            // Braille-pattern glyph. Name the action; hide the glyph from AT.
            //
            // ⚠️ FIXED 2026-09-09 UNDER A LIVE DESIGN TENSION (spec 121 gap 4) —
            // read this before "restoring" the light grey.
            //
            // It was `text-gray-300 hover:text-gray-500`. spec 116 recorded the
            // resting state as "#d1d5db, 1.422:1 on the thead's #fafbfc" and left it,
            // on the grounds that Jordan said "we can leave the dots" and that this
            // is one of three "minimal thin-grey" marks Himaad specified as a family.
            // Both of spec 116's numbers were wrong (stale Tailwind v3 hex; the
            // reports' thead is OPAQUE #f9fafb because ReportDataGrid passes
            // stickyHeader) — measured first-party, gray-300 is #d1d5dc and
            // 1.409:1 on #f9fafb, 1.421:1 on the list pages' #fafbfc.
            //
            // Why it could not stay: this span carries useSortable's `attributes`,
            // so it is a role="button" with tabIndex — a real keyboard-operable
            // control — and dragging it is the ONLY way to reorder a column in this
            // product (the Columns popover offers Freeze and Reset layout, nothing
            // else). SC 1.4.11 covers the visual information needed to IDENTIFY a
            // component, the resting glyph is the only such visual, and 1.409:1 is
            // not defensible for one. `hover:` cannot discharge identification: it
            // fires only once the pointer is already on the 24x24 box (there is no
            // group-hover reveal from the th), i.e. after the user has found what
            // they could not see. That is the same argument spec 116 §1 used to
            // REVERT round 2's "the resting hairline is decoration" position, one
            // file over and six days earlier; it applies here unchanged.
            //
            // ⚠️ AND IT CUTS AGAINST THE DESIGN INTENT, which is why this is an OPEN
            // QUESTION for Himaad and not a settled decision (spec 121 §4). Himaad,
            // 2026-09-03: "the grab handle, I feel like maybe it should stay hidden
            // until hover ... the table still stays a little much cleaner." Jordan
            // overruled the HIDING — "we can leave the dots, but lets make the
            // grabbable space much larger" — and neither of them asked for it to be
            // DARKER. Compliance makes it more prominent, which is the opposite of
            // what was asked for. Shipped at the minimum compliant colour available
            // as an existing token (--color-rc-muted #6b7280, 4.626:1 on #f9fafb):
            // nothing in @theme and no neutral Tailwind step sits between
            // --color-rc-control's failing 2.896:1 and this, so the true numeric
            // minimum (#8a929f, 3.002:1) would have been a brand-new token, and it
            // would also split this mark from the sort mark, which is ALREADY
            // text-rc-muted for the identical reason. If Himaad wants it lighter, the
            // honest route is a group-hover reveal from the header cell (close to
            // what he originally asked for) plus a disclosed near-miss at rest —
            // NOT a return to 1.409:1 on a control.
            //
            // hover:text-rc-ink-2 keeps the hover feedback the old pairing had; at
            // gray-500 (#6a7282, 4.628:1) it would have been indistinguishable from
            // the new resting colour, i.e. an inert utility of exactly the kind this
            // spec family exists to remove.
            aria-label={`Reorder ${headerLabel} column`}
            className={`${HANDLE_BOX} cursor-grab text-rc-muted hover:text-rc-ink-2 text-[10px] leading-none`}
            title="Drag to reorder"
          >
            <span aria-hidden="true">⠿</span>
          </span>
        )}
        {children}
      </div>
      {/* Column resize handle (CC-49380) — drag/touch to resize; double-click to
          auto-size; keyboard-operable (WCAG 2.1.1): focus + arrow keys nudge the
          width, Enter/Home resets.

          Himaad UI feedback B4 (2026-08-26) — the VISUAL and the HIT AREA are now
          decoupled, which is the whole point of the note.

          ⚠️ SUPERSEDED 2026-09-03 (spec 116, B1) — the next sentence of this note
          used to be load-bearing and is now historical. It read: "There is no
          vertical cell divider anywhere in any report grid, so this nub is the only
          vertical mark on screen." That was Himaad's own decision on 2026-08-26
          (spec 098) and HIMAAD HIMSELF REVERSED IT ON 2026-09-03: he asked for the
          divider back, "a shade or two lighter" and inset rather than "cutting…
          edge to edge", because "when the divider doesn't run edge to edge, like it
          still gives you separation, but it stops competing with the data." So a
          vertical divider now DOES exist in every FLAT report grid — a 16px centred
          1px hairline in --color-rc-control, drawn by this strip's ::after (see the
          geometry note further down). Not "decorative": the resting mark is the only
          thing identifying this control before contact, which is why round 2's attempt
          to call it ornament and lighten it was reverted on 2026-09-08 — see B1 below.
          Do not restore the 2026-08-26 rule; it is two designer decisions out of date.
          Both dates are here on purpose.

          The rest of B4's reasoning stands unchanged, and it is why the reversal is
          cheap: the nub it deleted was drawn as a 4px x 16px block (w-1 h-4) in #d1d5db —
          1.41:1 on the gray-50 header. A tabIndex=0 role="separator" carrying arrow-key
          handlers is a UI COMPONENT and owes SC 1.4.11 its 3:1, so a 1.41:1 mark was
          failing twice over: too heavy to read as a rule, too faint to read as a control.

          Now: the <span> ITSELF is the transparent grab strip and the ::after draws a
          1px hairline in --color-rc-control on the edge — thinner than B4's nub, with a
          target many times its width, and since 2026-09-03 16px and vertically centred
          rather than full-height (B1). Hover / focus / resizing thickens the mark to 2px
          in --color-rc-chip-text, the same blue as the focus rings, and lengthens it to
          24px.

          The strip STRADDLES the edge, 12px each side (Jordan, 2026-08-26). It grew
          inward-only at first and measured edge-12..edge-1 on the deployed page — 12px
          left, ZERO right — so aiming at the visible line missed it unless you came from
          the left. z-10 is load-bearing for the outward half: the strip overhangs into
          the NEXT th, which is later in DOM order and would otherwise win hit-testing
          there. That overhang lands inside the next cell's own px-4 padding, clear of
          its label, sort button and info icon.

          THE LAST HEADER IS THE EXCEPTION, and it is not hypothetical — it was measured
          failing. The scroll container is overflow-x-auto, so the final column's outward
          overhang adds phantom scrollable width: on /reports/190 it took scrollWidth to
          1110 against a clientWidth of 1098 and put a horizontal scrollbar on a grid that
          fits, which also trips the pinned-column scroll shadow. So the last header keeps
          the full 12px INWARD and no overhang (13px total). It is the one column with no
          neighbour to reach into, so nothing is lost. Keep this asymmetry: a uniform strip
          here is a scrollbar on every grid that exactly fits.

          B1 (Himaad, 2026-09-03, spec 116) — THE MARK IS NOW SPLIT, and the paragraph
          that used to sit here has been rewritten rather than deleted because its
          measurement was right and its conclusion was wrong.

          What it said: #8b95a5 is 3.03:1 on WHITE but 2.90:1 on the gray-50 (#f9fafb)
          header this actually sits on, i.e. a hair under the 3:1 floor — and therefore
          "do NOT fix it by lightening toward --color-rc-line, that would take a
          keyboard-operable control further backwards."

          What is true: re-measured 2026-09-03, #8b95a5 is L=0.2970 and 2.896:1 on
          #f9fafb, while a 3.0:1 mark on that fill may carry at most L=0.285 — so
          #8b95a5 is at 104.2% of the luminance budget and there is NO colour lighter
          than it that clears 3.0:1 here. The old note treated the token as a floor to
          defend; it was already below the floor. "Lighter AND 3.0:1" is not a tradeoff,
          it is an empty set, and Jordan's "best of both worlds" therefore cannot be a
          colour choice.

          SO ONLY HALF OF HIMAAD'S ASK IS TAKEN, and this is the second rewrite of this
          paragraph — round 2's version is recorded below it, marked wrong, because its
          conclusion shipped for a day and someone will otherwise re-derive it.

          What ships: the GEOMETRY changes and the COLOUR does not. `after:h-4` +
          `after:top-1/2` + `after:-translate-y-1/2` — a 16px vertically-centred
          hairline instead of the full-height `after:inset-y-0` one. That is what "not
          cutting… not edge to edge" literally asks for, and Himaad's own reason for
          asking ("it still gives you separation, but it stops competing with the
          data") is about LENGTH, not about the hex. The colour stays
          --color-rc-control, #8b95a5, 2.896:1 on this header.

          ⚠️ WHAT ROUND 2 DID AND WHY IT WAS WRONG (2026-09-03 → corrected 2026-09-08).
          It also took the resting colour to --color-rc-divider (#d1d5db, 1.410:1) and
          argued that the resting hairline had become DECORATION, so SC 1.4.11 no longer
          attached to it and was "carried instead by the STATE INDICATION" — hover /
          focus / resizing, which repaint the mark 2px in --color-rc-chip-text at
          6.413:1. The second half of that is true and still ships. The first half does
          not survive contact with the SC: 1.4.11 covers the information required to
          identify a component AND its states, and this hairline is the ONLY visual that
          the control exists before anyone interacts with it — the strip itself is
          bg-transparent. A mouse or touch user cannot hover, focus or touch a control
          they cannot locate, so the hover state cannot discharge the identify half. And
          reclassifying the sole resting affordance of a tabIndex=0 role="separator" with
          live key/mouse/touch handlers as ornament is the "decorative mark that is
          actually load-bearing for identifying a control" pattern, not an exemption
          from it. Net effect measured: 2.896:1 → 1.410:1, a 2.05x REGRESSION on a floor
          this repo gates on per PR, plus the mark shortening at the same time. Round 2
          was honest that touch lost out; what it missed is that the same argument
          applies to every sighted pointer user before first contact.

          So the tie-break goes the other way. "A shade or two lighter AND 3.0:1" is an
          empty set (the arithmetic above), and when Jordan asked for "best of both
          worlds" the half that cannot be given up is the floor, not the shade. Holding
          #8b95a5 delivers the visible half of the design change at NO accessibility
          cost — same ratio as origin/qa, and 2.5x the length of nothing.

          ⚠️ STATED OUT LOUD RATHER THAN CLOSED: 2.896:1 is still under 3.0:1. That is a
          PRE-EXISTING condition (origin/qa's full-height mark was the same 2.896:1, and
          the same hex on the same fill), it is not created or worsened here, and this
          change deliberately declines to make it worse. It is also not fixable from
          this line: #8b95a5 is at 104.2% of the luminance budget, so no colour clears
          3.0:1 in this position. THE ONLY LEVER IS THE HEADER FILL — darkening the
          thead is what makes a >=3.0:1 divider colour exist at all — and that is a
          design decision across every grid in the app, so it needs Himaad, not a
          drive-by. Until then the honest description of the resting mark is "2.896:1,
          a known and tracked near-miss", never "compliant" and never "decoration".

          The state indication is unchanged and still carries the STATE half of SC
          1.4.11 at 6.413:1, plus 24px on reveal (see the B1 x D2 note below). Those
          three states are pinned by gridChromeDividerHandle.test.jsx — NOT by
          headerAffordances.grids.test.jsx, which this comment used to cite and whose
          only ::after assertion is `after:content-['']`, true of every version of this
          className and therefore no guard on any of it.

          D2 (Himaad, 2026-08-27) — the HOVER-REVEALED GRIP, added on top of all of
          the above. Himaad asked for a grip on the divider, revealed on hover so the
          grid is not carrying a permanent handle on every column: you do not need
          the affordance until you are near the boundary. He owes the icon asset and
          has NOT delivered it, so this is a CSS stand-in — swap the lozenge for the
          asset when it lands and nothing else here has to move.

          Why it is a CHILD of the strip and not a sibling: e2e/reports-grid-audit.spec.js
          :663-667 counts `th [role="separator"]` and `th [class*="cursor-col-resize"]`
          and hard-fails on drift, and columnPinning.grids.test.jsx:163-165 asserts
          EXACTLY ONE [role="separator"] per header cell. An aria-hidden span with no
          role and no tabIndex adds neither — and it must stay unfocusable, because
          :678-700 requires document.activeElement's role to be "separator".

          Why no `group` on the th: the strip is already its own hover target across
          the full 25px straddle (today's hover:after:w-0.5 proves it), and a th-wide
          group would light the PREVIOUS column's grip while the pointer is in this
          column's left 12px. The group is scoped to the strip: `group/resize`.

          Revealed by hover OR focus-visible OR :active. focus-visible (not plain
          focus) is deliberate: TanStack's resize handler does not preventDefault, so
          a mouse-down focuses the strip, and plain `focus:` would leave the grip lit
          after the drag ends — the permanent-handle noise this exists to remove.
          :active covers mid-drag, when the pointer has left the strip but the button
          is still down; that is the same mechanism the grouped twin already relies on
          (see its comment: ":active = mid-drag, which is why this needs no isResizing
          state"). The ::after keeps its own separate isResizing branch below.

          The always-on hairline STAYS (B1 changed its height, not its colour and not
          its existence). Touch never gets hover — this strip carries touch-none and a
          live onTouchStart — so the hairline is still the only column-boundary
          affordance a touch user has before they touch it, and that is precisely why
          it still carries the IDENTIFY half of SC 1.4.11 at --color-rc-control's
          2.896:1 instead of being demoted to ornament at 1.410:1, which is what round 2
          did and 2026-09-08 reverted. See the B1 paragraph above. The grip is
          decorative — it does not identify the control, the hairline does — it merely
          annotates a boundary the hairline already draws, and it is drawn in
          --color-rc-chip-text, the same colour the hairline itself takes on hover,
          so the two read as ONE mark rather than as a blue line with a grey bead
          on it.

          Yes, 4x16px is the box B4 deleted, and that is not an accident: the SIZE was
          never the problem. What B4 removed was a PERMANENT 1.41:1 grey nub floating
          mid-cell standing in for a boundary that was not drawn. This is a hover-only
          6.41:1 thumb sitting on a boundary that now IS drawn (16px at rest, 24px on
          reveal). Do not reintroduce it as an always-on mark.

          Geometry: the ::after hairline thickens to 2px at exactly the moments the
          grip is visible, so the grip is centred on the THICKENED line — right-[11px]
          against after:right-[12px] w-0.5 puts both centres at 13px from the strip's
          right edge. The last header is the exception again: its line sits on the
          th's own right edge, so a centred grip would overhang by 1px, and outward
          overhang on the final column is what put a phantom scrollbar on a grid that
          fits (see above). It gets right-0 instead — flush inside the line, 1px
          off-centre, no overhang.

          B1 x D2, the collision, resolved deliberately: the grip is h-4 (16px) and
          B1's resting hairline is ALSO 16px, so if the hairline stayed 16px in the
          revealed states the 4px-wide lozenge would sit exactly on top of the 1-2px
          line at exactly the same height and COVER it end to end — a bead with no
          line to annotate, which is the "permanent nub standing in for a boundary
          that is not drawn" that B4 removed, re-created on hover. Two ways out:
          shrink the grip, or grow the line. The line grows. Hover / focus /
          resizing take the hairline to h-6 (24px) while the grip stays h-4, so the
          line extends 4px above and below the lozenge and the two read as a thumb
          ON a rule. It also means the reveal is now legible without hover styling
          at all — the mark lengthens as well as thickening and changing colour.
          24px fits: the th is py-3 (12px each side) on a 16px line box, so the
          revealed line still clears the cell's own text by 4px top and bottom. */}
      {header.column.getCanResize() && (
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${headerLabel} column`}
          aria-valuenow={Math.round(header.column.getSize())}
          tabIndex={0}
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          onDoubleClick={() => header.column.resetSize()}
          onKeyDown={(e) => {
            const table = header.getContext().table
            const id = header.column.id
            const cur = header.column.getSize()
            const step = e.shiftKey ? 24 : 8
            if (e.key === 'ArrowLeft') {
              e.preventDefault()
              table.setColumnSizing((s) => ({ ...s, [id]: Math.max(40, cur - step) }))
            } else if (e.key === 'ArrowRight') {
              e.preventDefault()
              table.setColumnSizing((s) => ({ ...s, [id]: cur + step }))
            } else if (e.key === 'Enter' || e.key === 'Home') {
              e.preventDefault()
              header.column.resetSize()
            }
          }}
          title="Drag to resize · double-click to auto-fit · arrow keys to adjust"
          /* The <span> IS the grab strip — 17px wide, transparent, STRADDLING the
             column edge — and the ::after draws the 1px visual line on the edge
             itself. Round 1 hung the target off ONE side (before:-left-[10px],
             right:0), so measured live the hit area ran from edge-12 to edge-1 and
             was 0px to the right: aiming at the line missed it by a pixel unless
             you approached from the left. -right-[8px] w-[17px] centres it, so it
             is ±8px either side of the line.
             z-10 is load-bearing: the strip overhangs into the NEXT th, which is
             later in DOM order and would otherwise win hit-testing on that half.
             The 8px overhang lands inside that th's own px-4 padding, so it never
             covers a label, a sort button or the info icon.

             `focus:outline-none` is NOT suppressing the keyboard focus ring, which
             is worth stating because it reads exactly as if it were. It cannot:
             index.css is entirely UNLAYERED while Tailwind v4 emits utilities inside
             @layer utilities, and unlayered rules beat every layer regardless of
             specificity — so index.css:261 `:focus-visible { outline: 2px solid
             var(--color-cb-navy); outline-offset: 2px }` wins on keyboard focus.
             Different selectors as well as different layers: this utility is
             `:focus`, which is also what a MOUSE-down on the strip triggers (
             TanStack's resize handler does not preventDefault), and suppressing the
             ring for that case is the only thing it is doing. --color-cb-navy is
             #09203d (index.css:27) and #09203d on #f9fafb is 15.63:1, so SC 2.4.7 is
             met by the unlayered rule and neither of these lines should be "cleaned
             up" into the other.
             (⚠️ CORRECTED 2026-09-08: this said "#00285c … 15.6:1" and cited
             index.css:200. The RATIO was right for the real token, so no pixel and no
             conclusion moves — but #00285c is a ReportChart palette entry, 13.78:1,
             and greping it to re-verify this argument lands in the chart colours.
             src/index.css is the authority on token hexes; CLAUDE.md's have gone stale
             before, and so had this comment's.)

             B1's resting/revealed split lives in the ternary below rather than in a
             base class plus a variant, on purpose: `after:h-4` and `after:h-6` (or
             `after:w-px` and `after:w-0.5`) in ONE class attribute are same-layer,
             same-specificity utilities, so which wins is decided by Tailwind's own
             emission order, not by the order they are written here. Making the two
             states mutually exclusive strings means there is nothing to resolve. */
          className={`group/resize absolute top-0 h-full z-10 ${isLastHeader ? 'w-[13px] right-0 after:right-0' : 'w-[25px] -right-[12px] after:right-[12px]'} cursor-col-resize touch-none select-none bg-transparent focus:outline-none ${DIVIDER_BASE} hover:after:h-6 hover:after:w-0.5 hover:after:bg-rc-chip-text focus:after:h-6 focus:after:w-0.5 focus:after:bg-rc-chip-text ${header.column.getIsResizing() ? 'after:h-6 after:w-0.5 after:bg-rc-chip-text' : DIVIDER_RESTING}`}
        >
          {/* D2's grip. Swap this span's body/background for Himaad's icon when he
              delivers it; the positioning and the reveal stay.

              ⚠️ THE PARITY CLAIM THAT USED TO BE HERE IS NOW HALF FALSE, and it is
              the one comment in this file spec 116 round 2 left asserting something
              its own diff had just broken. It read: "Byte-identical to
              ReportGroupedGrid.jsx's copy — change one, change the other, or the 33
              flat reports get a different divider from the 86 grouped ones."

              ✅ CLOSED 2026-09-09 (spec 121 gap 1). The divergence this comment
              described is gone, and the record of it stays rather than being deleted:
                - 2026-09-03 → 2026-09-08, flat was `after:top-1/2
                  after:-translate-y-1/2 after:h-4` (16px, centred) while grouped was
                  `after:inset-y-0` (full height) with NO after:h-* token in any
                  state, and HANDLE_BOX did not exist in the grouped grid at all, so
                  the 86 grouped reports kept a ~7-10px reorder target against this
                  file's 24 x 24.
                - Now: the resting mark's positioning and geometry are INTERPOLATED
                  from gridHeaderChrome.js's DIVIDER_BASE + DIVIDER_RESTING in both
                  files, so they cannot drift by editing one; the grouped grid's
                  reveal gained the matching `after:h-6` on all three of its states;
                  and its handle and frozen marker take HANDLE_BOX_PX3 (the same box,
                  minus a -ml-[3px] that is wrong for a px-3 th — see that module).
                - The GRIP span below is still byte-identical to the grouped copy.
                - Still genuinely different, and NOT closable: this grid reveals off
                  `getIsResizing()` (TanStack owns its resize plumbing) and the
                  grouped one off `:active` (it hand-rolls resize and has no such
                  state). So an anti-drift test demanding byte-identical classNames
                  would be WRONG and would get weakened rather than fixed. What is
                  pinned instead, per renderer, is the PROPERTY: every state that
                  thickens the mark must also lengthen it, and the resting colour and
                  height come from the shared constants. That assertion is now in
                  headerAffordances.grids.test.jsx, whose only ::after assertion used
                  to be `after:content-['']` — true of every version of both
                  classNames, so it guarded none of this. CI still never runs the
                  frontend suite, so a local run is the only gate. */}
          <span
            aria-hidden="true"
            className={`pointer-events-none absolute top-1/2 h-4 w-1 -translate-y-1/2 rounded-full bg-rc-chip-text opacity-0 transition-opacity ${isLastHeader ? 'right-0' : 'right-[11px]'} group-hover/resize:opacity-100 group-focus-visible/resize:opacity-100 group-active/resize:opacity-100`}
          />
        </span>
      )}
    </th>
  )
}

/**
 * What to call a column in the Columns picker (CC-50288).
 *
 * A column whose `header` is a STRING renders that string — but an action column
 * declares `header: ''` so the grid draws no heading above the buttons, and `''`
 * IS a string, so the old `typeof header === 'string' ? header : column.id` test
 * chose it and the picker drew a checkbox with no words next to it. That is what
 * QA saw on the Report Queue, and it is also an accessible-name failure: a
 * checkbox whose only label is empty has nothing for a screen reader to announce.
 *
 * Order: an explicit `meta.pickerLabel` (say what a header-less column is), then
 * a non-blank string header, then the column id as a last resort — never ''.
 */
function columnPickerLabel(column) {
  const def = column.columnDef || {}
  const explicit = def.meta?.pickerLabel
  if (typeof explicit === 'string' && explicit.trim()) return explicit
  if (typeof def.header === 'string' && def.header.trim()) return def.header
  return column.id
}

/*
 * The LIST-PAGE Columns picker: every DataGrid without `reportChrome` (Report
 * Queue, Recurring schedules, Matters, Contacts and the other non-report list
 * pages). Report grids never render this; theirs is Display View, below.
 *
 * spec 181 (CC-53473). This used to be an `absolute` child of the grid card, and
 * the card is `overflow-hidden` (the CC-50280 kebab trap again). It was visible
 * only because a table with rows makes the card taller than the menu. Untick
 * every column and the card shrank to its 49px toolbar row while the menu stayed
 * 234px tall: on Recurring schedules 0 of 7 checkboxes could be clicked, on the
 * Queue 2 of 6. Visibility persists per grid, so a reload changed nothing, and
 * neither page has Reset layout. Three changes, each enough on its own to give
 * the user a way back:
 *
 *   1. It renders through PopoverMenu: a portal to <body> plus position: fixed,
 *      which no card can clip however short it gets.
 *   2. The last visible data column cannot be unticked (`lastVisibleColumnId`),
 *      and the picker says why in words.
 *   3. A stored "every column hidden" map is read as "nothing hidden" (see the
 *      `columnVisibility` initialiser in DataGrid).
 *
 * role="dialog", not "menu". The rows are native checkboxes (ControlCheckbox, for
 * the keyboard and DOM-shape reasons in its docblock), and an ARIA menu may only
 * own menuitem rows, so the old role="menu" was itself wrong. PopoverMenu's dialog
 * mode is the one built for real controls: no arrow-key roving to eat keys; focus
 * moves to the first checkbox on open, which it MUST now that the popover sits at
 * the end of <body> and Tab from the trigger no longer leads into it; Escape
 * closes and returns focus to the trigger; a click outside closes; ticking a box
 * leaves it open. Its click-outside test excludes the popover itself, so a press
 * on a checkbox is not mistaken for "outside" (the portal trap that once killed
 * KebabMenu's onSelect). `tabOutToTrigger` keeps the page's Tab order the in-flow
 * menu had: Tab past the last box goes on to whatever follows Columns, and
 * Shift+Tab before the first returns to Columns, instead of focus falling off the
 * end of the document where the portal lives.
 *
 * `width={null}` leaves the popover as wide as its longest label, exactly as the
 * old shrink-to-fit menu was (min 180px, 250px with alignment): PopoverMenu only
 * writes an inline width when given one, and it measures the real rendered width
 * to place itself. `max-h` + scroll because a fixed panel cannot grow past the
 * bottom of the viewport the way the in-flow menu pushed the page down.
 */
function ColumnVisibilityDropdown({ table, enableAlignment = false, columnAlignment = {}, onColumnAlignmentChange }) {
  const guardNoteId = useId()
  const leafColumns = table.getAllLeafColumns()
  const lockedId = lastVisibleColumnId(leafColumns)

  return (
    <PopoverMenu
      role="dialog"
      label="Show or hide columns"
      triggerLabel="Toggle column visibility"
      title="Show or hide columns"
      triggerClassName="btn-cb-toolbar"
      width={null}
      tabOutToTrigger
      triggerContent={
        <>
        <svg
          className="w-3.5 h-3.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        Columns
        </>
      }
    >
        <div className={`max-h-[60vh] overflow-y-auto ${enableAlignment ? 'min-w-[250px]' : 'min-w-[180px]'}`}>
          {leafColumns.map((column) => {
            const label = columnPickerLabel(column)
            // spec 181: the one data column still showing. Unticking it would
            // leave a blank list, so the box refuses (see `locked` below).
            const locked = column.id === lockedId
            return (
              <label
                key={column.id}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 ${locked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {/* ⚠️ CORRECTED 2026-09-03 (spec 116). This used to read:
                    "border-rc-control, not border-gray-300 (2026-08-26 ADA sweep…).
                    An UNCHECKED checkbox is white-on-white: its 1px border is the
                    only thing on screen saying a control is there, so SC 1.4.11
                    wants 3:1. #d1d5db was 1.47:1; #8b95a5 is 3.03:1."

                    The REASONING was right and is now implemented. The CLAIM was
                    false: `border-rc-control` with no bare `border` width utility
                    beside it painted nothing at all, because Tailwind v4's preflight
                    sets border-width: 0 on `*`. So the 2026-08-26 edit changed
                    1.47:1 to… still no border, and the sweep recorded a contrast fix
                    that never reached a screen. `text-cb-accent-text` and
                    `focus:ring-cb-accent` were inert here too — see ControlCheckbox.

                    ControlCheckbox draws the box itself with a real 1px border, so
                    something is actually rendered now. NOT 3.03:1, though — that is
                    #8b95a5 on WHITE, and this picker row is `hover:bg-gray-50`
                    (#f9fafb) exactly while a mouse user is pointing at it, where the
                    same hex is 2.896:1. Round 2 shipped that and claimed the floor was
                    met; 2026-09-08 moved the unchecked border to --color-rc-seam
                    (#6b7280), which is 4.834 / 4.666 / 4.626 on white / the grid thead
                    / this hover fill. See ControlCheckbox's docblock. The 4px-ish radius
                    stays (rounded-[3px] on a 14px box): B2's 6px control radius is
                    button language and reads as a circle at this size.

                    2026-09-09 (spec 121 gap 3): this was `<GridCheckbox>`, a
                    byte-identical second copy of the same control that lived in this
                    file. It is now the shared `ControlCheckbox`. No `hit24` here —
                    the row <label> above already supplies the target.

                    spec 181: a LOCKED box is aria-disabled, not `disabled`. A
                    disabled input cannot take focus, so when the locked column is
                    the first row, PopoverMenu's focus-on-open would land nowhere
                    and a keyboard user could not get into the popover at all (Tab
                    from the trigger no longer leads there). aria-disabled keeps it
                    focusable and announced as unavailable, with the reason as its
                    description; the onChange guard is what actually refuses, and
                    React puts the controlled `checked` back. Dimmed through the
                    wrapper, because ControlCheckbox's own dimming keys off
                    :disabled. */}
                <ControlCheckbox
                  checked={column.getIsVisible()}
                  onChange={(e) => {
                    if (locked) return
                    column.getToggleVisibilityHandler()(e)
                  }}
                  aria-disabled={locked || undefined}
                  aria-describedby={locked ? guardNoteId : undefined}
                  className={locked ? 'opacity-50' : ''}
                />
                <span className="flex-1 min-w-0 truncate">{label}</span>
                {/* Per-column alignment override (CC-49735) — Auto = the
                    report's own metadata alignment; persists like widths.
                    2026-08-26: border-rc-control + rounded-rc-control, matching the
                    report-chrome copy of this control in Display View. It was
                    border-gray-200 (1.24:1) — the DECORATIVE token on an interactive
                    element, which is exactly what index.css says never to do. */}
                {enableAlignment && (
                  <select
                    aria-label={`Alignment for ${label}`}
                    value={columnAlignment[column.id] || ''}
                    onChange={(e) => onColumnAlignmentChange?.(column.id, e.target.value || null)}
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs border border-rc-control rounded-rc-control px-1.5 py-1 text-gray-600 focus:ring-2 focus:ring-cb-accent/20 focus:border-cb-accent"
                  >
                    <option value="">Auto</option>
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                )}
              </label>
            )
          })}
        </div>
        {/* spec 181: why the last box will not untick, said where it happens.
            A polite live region that is always mounted, so the sentence is
            announced the moment unticking the second-to-last column engages
            the guard (focus is on that other box, not on this one). An
            aria-live attribute, NOT role="status": DataGrid pages already
            carry dnd-kit's status region and a second one makes
            getByRole('status') ambiguous on every adopting page. */}
        <div aria-live="polite">
          {lockedId && (
            <p id={guardNoteId} className="mt-1 border-t border-gray-100 px-3 pt-2 text-xs text-gray-500">
              At least one column must stay visible.
            </p>
          )}
        </div>
    </PopoverMenu>
  )
}

// Per-column header filter — thin wrapper over the shared ColumnValuePicker
// (CC-49317). Free-text always; columns that supply `meta.pickerValues` also get
// the multi-select picker (value becomes { text, picked }). Other DataGrid
// consumers don't set pickerValues, so they keep the plain string value.
function ColumnFilterCell({ header }) {
  const column = header.column
  if (!column.getCanFilter()) return null
  const filterLabel = typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id
  return (
    <ColumnValuePicker
      label={filterLabel}
      value={column.getFilterValue()}
      pickerValues={column.columnDef.meta?.pickerValues}
      onChange={(v) => column.setFilterValue(v)}
    />
  )
}

/*
 * A stable empty array for a read that has not resolved. TanStack Table needs an
 * array on every render, and handing it a fresh `[]` each time is needless work.
 */
const NO_ROWS = []

const DataGrid = ({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No data available',
  /*
   * CC-53132. What to say when the read did not resolve, as distinct from
   * resolving to nothing. Callers with a domain voice ("No unapplied payments"
   * vs this) should override it; the default is deliberately honest rather than
   * reassuring.
   */
  unresolvedMessage = 'This list could not be loaded, so it is not known to be empty. Reload to try again.',
  loadingMessage = 'Loading data...',
  onRowClick,
  pageSize: pageSizeProp,
  enableExport = true,
  enableFiltering = true,
  // spec 092 — report chrome ("Bar 3"). OFF by default, so the nine non-report
  // list pages that share this grid are untouched.
  reportChrome = false,
  gridNote,             // the mock's muted note beside the row count
  headerRow,            // grid-owned controls on their own row above the count
  displayViewExtras,    // the report grid's own display toggles, folded into Display View
  /*
   * E4 (2026-08-27) — "Reset grid layout" must reset the View options sitting
   * beside it IN THE SAME MENU, and this grid cannot do that itself.
   *
   * `displayViewExtras` is an already-rendered node: the state behind those
   * checkboxes (hide $0, hide inactive, show $, the aging window) lives in
   * ReportDataGrid, and DataGrid is the GENERIC list grid — nine non-report
   * pages share it, so it must not grow report-specific state to reach them.
   * So the owner of the state hands down the reset, and this grid calls it as
   * part of its own reset. Optional: a host that contributes no view options
   * (every non-report page) passes nothing and nothing changes.
   *
   * Why this matters more than the wording: a user who ticks "Hide $0 /
   * zero-balance rows" and then clicks Reset two rows below it was left still
   * hiding rows on a WIP or A/R grid while believing they had reset it. Rows
   * silently missing from a financial grid is a wrong number.
   */
  onResetDisplayOptions,
  stripActions,         // host-owned buttons for the strip, LEFT of Display View
  enableSelection = false,
  onSelectionChange,
  bulkActions,
  exportFilename = 'export',
  storageKey,
  onVisibleRowsChange,
  // Opt-in (report grids only) — column resize + persisted widths + the reset
  // control ("Reset grid layout" under reportChrome, "Reset layout" without it;
  // E4 renamed only the former, so naming one label here would mislead).
  // Kept off by default so the ~25 other list pages are unchanged (CC-49380 scope).
  enableLayout = false,
  // Opt-in polish (report grids only): frozen header on a fixed-height scroll
  // region, and readable sentence-case headers instead of cramped ALL-CAPS.
  // Default off → list pages unchanged.
  stickyHeader = false,
  normalCaseHeaders = false,
  rowNoun = 'row', // toolbar count noun (e.g. 'reconciliation') for domain parity
  // Stable per-row test handle, opt-in. See the note at the <tr>.
  rowTestId = undefined,
  // CC-49329: metadata-driven default sort ([{ id, desc }]) applied when the
  // user has no persisted sort for this page.
  defaultSorting = [],
  // CC-49735 (report grids only): per-column alignment override control in the
  // Columns dropdown. `columnAlignment` = the override map ({ colId: align }),
  // resolution against report metadata stays with the caller (via meta.align).
  enableAlignment = false,
  columnAlignment,
  onColumnAlignmentChange,
  // CC-49735: totals rendered as a real footer row aligned column-under-column
  // (like the grouped grid's tfoot). Map of column id → ReactNode; columns
  // without an entry render an empty footer cell. Off (undefined) by default.
  footerCells,
  // CC-49732: seed the per-column quick-filters (TanStack [{ id, value }] shape)
  // when a saved view restores them. Read once on mount — the grid remounts on
  // view apply, so this never fights live user edits.
  initialColumnFilters,
  // CC-49462 (report grids only): freeze the report's identity column at the
  // left edge so it stays readable on a wide report. Off by default so the ~25
  // list pages are untouched; see src/utils/columnPinning.js for the rules.
  enablePinning = false,
  // CC-50262: keep the user's page when `data` is replaced by a background
  // refresh. For a POLLED grid (the Report Queue refetches every 5s) TanStack's
  // default behaviour throws the reader back to page 1 unprompted. Opt-in, and
  // default OFF, because switching it on also removes a render the report grids'
  // totals strip currently relies on — see the note at `autoResetPageIndex` below.
  keepPageOnDataChange = false,
  // CC-50173: the table's accessible name. Defaults from `rowNoun` so all ~25
  // existing call sites get a real name without being touched ("row" → "Data
  // table"); a caller with a better one (a report's own title) passes it.
  tableLabel: tableLabelProp,
}) => {
  const tableLabel = tableLabelProp || (rowNoun && rowNoun !== 'row' ? `${rowNoun} table` : 'Data table')
  const prefsStore = usePreferencesStore()
  // With a storageKey the stored per-page choice wins, but `pageSize` is now the
  // fallback rather than being ignored outright — previously any page with a
  // storageKey silently got the global default of 50, which is why the Report
  // Queue could never show a pager (its list is capped at 25 rows).
  const resolvedPageSize = storageKey
    ? prefsStore.getPageSize(storageKey, pageSizeProp)
    : (pageSizeProp ?? 50)

  // Sort persists per page when layout persistence is on (CC-49336 — named views
  // capture/restore it; also means a report reopens the way you left it).
  // Optional-chained store reads keep older test mocks (without the new
  // methods) working.
  const [sorting, setSorting] = useState(() => {
    const stored = enableLayout && storageKey ? prefsStore.getSorting?.(storageKey) : null
    if (Array.isArray(stored) && stored.length > 0) return stored
    return defaultSorting.length > 0 ? defaultSorting : []
  })
  // spec 181 (CC-53473): on a LIST page, a stored map that hides every column is
  // read as "nothing hidden". Users already stuck on a blank Queue / Recurring
  // schedules list (written before the picker refused the last column) land on
  // a working list again, and the mount-time write below stores the healed map.
  // Report grids (reportChrome) are read exactly as before: their Display View
  // popover was never clipped, so an all-hidden report grid is still a choice
  // the user can undo where they made it.
  const [columnVisibility, setColumnVisibility] = useState(() => {
    if (!storageKey) return {}
    const stored = prefsStore.getColumnVisibility(storageKey)
    return reportChrome ? stored : recoverEveryColumnHidden(stored, columns.map((c) => c.id || c.accessorKey))
  })
  // spec 092: under report chrome the per-column filter row is a labelled toggle
  // instead of a permanent row of unnamed inputs. Starts closed there (the mock's
  // grid has none); always open everywhere else, exactly as before.
  //
  // This is the user's EXPLICIT choice only. What the row actually does is
  // `filterRowVisible` below, which also opens for an already-active filter.
  const [filterRowOpen, setFilterRowOpen] = useState(!reportChrome)
  const [columnFilters, setColumnFilters] = useState(() =>
    Array.isArray(initialColumnFilters) && initialColumnFilters.length > 0 ? initialColumnFilters : []
  )
  /*
   * A SEEDED filter must reveal its own row, and the toggle must say how many.
   *
   * `initialColumnFilters` (from a saved view, ReportDataGrid.jsx:672) seeds
   * `columnFilters` on mount while `filterRowOpen` starts FALSE under report
   * chrome — so opening a saved view on any of the 33 flat reports rendered a
   * FILTERED money grid with the filter row off screen and a bare "Column
   * Filter" label claiming nothing. The reader had no way to know the WIP / A/R
   * figure in front of them was scoped. The grouped grid has never had this bug:
   * ReportGroupedGrid.jsx:570 derives `filtersVisible = showFilters ||
   * activeFilterCount > 0` and :1439 appends ` (N)`.
   *
   * So this is the grouped grid's VISIBILITY rule and its copy exactly (the
   * count PREDICATE differs, and has to — see below):
   * derived, not a one-shot initial state, which also means the row cannot be
   * dismissed while a filter is live. That is the point: on a financial grid the
   * control that scopes the money is not something the user can hide from
   * themselves.
   *
   * The count format is EXACTLY ` (${n})`, matching ReportGroupedGrid.jsx:1439,
   * because both grids share one e2e locator (`/^Column Filter/`,
   * e2e/reports-grid-audit.spec.js:577) and one unit helper
   * (`openColumnFilters`, src/test/gridChrome.js) — a second format would make
   * the next locator match one grid and silently skip the other.
   *
   * IT IS `.length`, NOT the grouped grid's non-empty predicate, and not
   * `filterIsActive` from reportGridUtils.js — because the two grids run
   * different filter ENGINES and the count must describe the engine it is
   * labelling. The grouped grid decides per row via `matchColumnFilter`, which
   * returns true (scopes nothing) for an inactive value, so skipping those in
   * its count is accurate there. TanStack applies EVERY entry in
   * `columnFilters` — `getFilteredRowModel` resolves each id to a filterFn with
   * no emptiness check — so here an entry is scoping the grid whether or not its
   * value looks meaningful. Measured 2026-08-27: a seeded
   * `{ value: { text: '', picked: [] } }` stringifies to "[object Object]"
   * inside `includesString` and takes the grid to ZERO rows. A predicate that
   * called that "inactive" would report 0, leave the row closed, and put an
   * empty money grid on screen with nothing explaining it — this defect again,
   * one layer down.
   *
   * The live path cannot produce such an entry (TanStack auto-removes `''` and
   * `undefined` on write), so `.length` equals the number of real filters in
   * normal use; a seeded view is the only divergence, and there over-counting is
   * the safe direction — it opens the row and shows the user the entry.
   */
  const activeFilterCount = columnFilters.length
  const filterRowVisible = filterRowOpen || activeFilterCount > 0
  // Column ORDER persists per page too (CC-49459 parity: "widths/order persist").
  // A stored order is validated against the current column ids — stale entries
  // are dropped, new columns appended in their default position.
  // Memoized because the pin resolution below (CC-49462) memoizes on it — an
  // array rebuilt every render would re-resolve the pin on every render.
  const defaultOrder = useMemo(() => columns.map((c) => c.id || c.accessorKey), [columns])
  const [columnOrder, setColumnOrder] = useState(() => {
    const stored = enableLayout && storageKey ? prefsStore.getColumnOrder?.(storageKey) : null
    if (!Array.isArray(stored) || stored.length === 0) return defaultOrder
    const valid = stored.filter((id) => defaultOrder.includes(id))
    const missing = defaultOrder.filter((id) => !valid.includes(id))
    return [...valid, ...missing]
  })
  const [columnSizing, setColumnSizing] = useState(() =>
    storageKey ? prefsStore.getColumnWidths(storageKey) : {}
  )
  const [rowSelection, setRowSelection] = useState({})

  // CC-49462: which column is frozen at the left edge. Stored per report as a
  // `pin:<columnId>` page flag, so an explicit choice survives reorder / hide /
  // rename, and the reset clears it with the rest of the page's preferences —
  // both copies, "Reset grid layout" and "Reset layout", via handleResetLayout.
  // Resolved against `columnOrder` — the user's CURRENT order — so the unset
  // default freezes whatever is already leftmost instead of overriding a
  // persisted drag-reorder (CC-49459). Optional chaining keeps older test mocks
  // (no getPageFlag) working: they see no stored choice and get the default.
  //
  // Resolution is scoped to the columns the user can SEE (EX7): a pin on a
  // hidden column renders no cell, so counting it would leave the toggle
  // claiming "frozen" over a grid that is not frozen.
  const isColumnVisible = useCallback((id) => columnVisibility[id] !== false, [columnVisibility])
  const pinnedColumnId = useMemo(
    () =>
      enablePinning && storageKey
        ? resolvePinnedColumnId(
            columnOrder,
            (flag) => prefsStore.getPageFlag?.(storageKey, flag),
            isColumnVisible
          )
        : null,
    [enablePinning, storageKey, columnOrder, prefsStore, isColumnVisible]
  )
  // The order actually rendered: the frozen column hoisted to the left edge.
  const effectiveOrder = useMemo(
    () => orderWithPinFirst(columnOrder, pinnedColumnId),
    [columnOrder, pinnedColumnId]
  )
  // What "first column" means for the Freeze button — the leftmost VISIBLE
  // column, so the button can never write a pin the resolver will refuse.
  const pinTargetId = useMemo(
    () => pinToggleTargetId(columnOrder, isColumnVisible),
    [columnOrder, isColumnVisible]
  )
  const handleTogglePin = useCallback(() => {
    if (!storageKey) return
    const readFlag = (flag) => prefsStore.getPageFlag?.(storageKey, flag)
    // spec 094 — emitted HERE, not around setPageFlag: unfreezing writes one
    // flag per column in the loop below, so a store-level wrapper would emit N
    // events for one click.
    trackGridTransform(storageKey, 'column_pin', pinnedColumnId ? 'unfreeze' : 'freeze')
    if (pinnedColumnId) {
      // Explicit OFF, recorded per column so it reads as a choice, not "unset".
      // Any pin still stored against a hidden column is cleared with it, or
      // showing that column again would re-freeze the grid on its own.
      for (const id of pinFlagsToClearOnUnfreeze(columnOrder, readFlag, pinnedColumnId)) {
        prefsStore.setPageFlag?.(storageKey, pinFlagName(id), false)
      }
      return
    }
    if (pinTargetId) prefsStore.setPageFlag?.(storageKey, pinFlagName(pinTargetId), true)
  }, [storageKey, pinnedColumnId, pinTargetId, columnOrder, prefsStore])
  // Reveals the frozen column's divider only once the grid is scrolled right.
  const scrollRef = usePinnedScrollShadow(!!pinnedColumnId)

  useEffect(() => {
    if (storageKey) {
      prefsStore.setColumnVisibility(storageKey, columnVisibility)
    }
  }, [columnVisibility, storageKey])

  // Persist column widths per report/page (CC-49380).
  useEffect(() => {
    if (storageKey) {
      prefsStore.setColumnWidths(storageKey, columnSizing)
    }
  }, [columnSizing, storageKey])

  // Persist order + sort per report/page (CC-49459 / CC-49336) — layout pages only.
  useEffect(() => {
    if (enableLayout && storageKey) prefsStore.setColumnOrder?.(storageKey, columnOrder)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnOrder, storageKey, enableLayout])
  // CC-49948: persist the sort only once the USER has changed it. This effect
  // used to fire on mount too, which wrote the metadata-derived default sort
  // into preferences as though it had been chosen — so the next visit read it
  // back from storage and the metadata rule never ran again. That is why the
  // CC-49948 default-sort fix looked inert in QA: the old (wrong) default had
  // already been frozen into localStorage by an earlier visit. Skipping the
  // mount write keeps an unchosen default out of storage; the store migration
  // (preferencesStore v1) clears the ones already written.
  const sortingPersistedOnce = useRef(false)
  useEffect(() => {
    if (!sortingPersistedOnce.current) {
      sortingPersistedOnce.current = true
      return
    }
    if (enableLayout && storageKey) prefsStore.setSorting?.(storageKey, sorting)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorting, storageKey, enableLayout])

  // Reset this page's layout to defaults (CC-49380): clears persisted visibility +
  // widths and resets in-memory order/sort/sizing.
  //
  // The pin (CC-49462) is reset for free and still is: it is a `pin:<columnId>`
  // entry in the store's generic `pageFlags` map, and `resetPagePreferences`
  // deletes `pageFlags[page]` wholesale — re-verified 2026-08-28 at
  // src/store/preferencesStore.js:157. There is no separate unpin call to add
  // here. (:155 of the same block drops the stored currency symbol, which is
  // why the E4 call below only has React state left to fix.)
  const handleResetLayout = useCallback(() => {
    trackGridTransform(storageKey, 'reset_layout')
    if (storageKey) prefsStore.resetPagePreferences(storageKey)
    setColumnVisibility({})
    setColumnSizing({})
    setSorting([])
    setColumnFilters([])
    setColumnOrder(columns.map((c) => c.id || c.accessorKey))
    // E4: the View options in the same menu. LAST, and via the host, because
    // this grid does not own that state — see the prop's note above. It has to
    // be called even though `resetPagePreferences` already dropped the stored
    // currency symbol: the store write and the React state are separate, and
    // clearing only the store would leave the grid still forcing/stripping `$`
    // with nothing persisted to explain it after a reload.
    onResetDisplayOptions?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, columns, prefsStore, onResetDisplayOptions])

  // Optional: surface the current filtered rows to a parent (e.g. ReportDataGrid's
  // totals strip needs them to recompute SUM-only totals over the visible set).
  // Held in a ref so the callback's identity doesn't pull into the deps array.
  const onVisibleRowsChangeRef = useRef(onVisibleRowsChange)
  useEffect(() => {
    onVisibleRowsChangeRef.current = onVisibleRowsChange
  })
  useEffect(() => {
    if (!onVisibleRowsChangeRef.current) return
    // Publish the rows the user actually sees — filtered AND sorted, in display
    // order — so "export what you see" honors the current sort, plus the ordered
    // visible column ids. Read fresh; changes on filter/sort/data/visibility/order.
    const sortedFiltered = table.getSortedRowModel().rows.map((r) => r.original)
    const visibleColumnIds = table.getVisibleLeafColumns().map((c) => c.id).filter((id) => id !== '_select')
    // Third arg (CC-49732): the active per-column filters so a saved view can
    // capture them. Additive — existing consumers ignore it.
    // Fourth arg (CC-50311): the `data` these rows were derived FROM. This effect
    // runs one commit AFTER `data` changes, so a parent that recomputes financial
    // totals from the published rows otherwise cannot tell "the user filtered"
    // from "the publication hasn't caught up yet" — and re-summing a stale row
    // set renders a wrong money figure (Trial Balance showed $0.00 debits and
    // credits over two real rows). Passing the source array lets the parent
    // compare identities and fall back to CB's authoritative server total.
    // Additive — existing consumers ignore it.
    onVisibleRowsChangeRef.current(sortedFiltered, visibleColumnIds, columnFilters, data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, data, columnVisibility, columnOrder])

  useEffect(() => {
    if (onSelectionChange) {
      const selectedRows = Object.keys(rowSelection)
        .filter((k) => rowSelection[k])
        .map((idx) => tableData[parseInt(idx)])
        .filter(Boolean)
      onSelectionChange(selectedRows)
    }
  }, [rowSelection])

  const selectionColumn = enableSelection
    ? [{
        id: '_select',
        size: 40,
        enableSorting: false,
        enableColumnFilter: false,
        // spec 116: the same drawn checkbox as the Columns picker. Row selection is a
        // real interactive control, so what had to survive the swap: the accessible
        // name (still the `aria-label` on the input itself, unchanged), keyboard
        // operation (still a native input — Space toggles), checked state exposed to
        // AT (still native, so `aria-checked` is implicit and correct), the
        // stopPropagation that keeps a selection click from opening the row, and the
        // DOM shape several page suites count with
        // `querySelectorAll('input[type="checkbox"]')` — the input is still there,
        // still first, just transparent over a drawn box.
        //
        // 2026-09-09 (spec 121 gap 3): migrated from this file's own `GridCheckbox`
        // to the shared `ControlCheckbox`, which is now the only drawn checkbox in
        // the tree. `hit24` IS REQUIRED at these two sites and nowhere else in the
        // migration: they are the only drawn-checkbox call sites with no wrapping
        // <label>, so they cannot inherit a target and the component's default 14x14
        // input would be a silent SC 2.5.8 regression that looks identical on screen.
        // `label=` became `aria-label=` — same accessible name, forwarded to the
        // input by the component's rest spread, so `getByRole('checkbox', { name })`
        // is unchanged.
        header: ({ table: t }) => (
          <ControlCheckbox
            hit24
            aria-label="Select all rows"
            checked={t.getIsAllPageRowsSelected()}
            onChange={t.getToggleAllPageRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <ControlCheckbox
            hit24
            aria-label={`Select row ${row.index + 1}`}
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
            onClick={(e) => e.stopPropagation()}
          />
        ),
      }]
    : []

  const allColumns = useMemo(
    () => [...selectionColumn, ...columns],
    [columns, enableSelection]
  )

  /*
   * CC-53132: `undefined` and `[]` are NOT the same answer.
   *
   * react-query hands back `data === undefined` for a read that failed, was
   * paused (`fetchStatus: 'paused'` — queryFn never called, nothing logged) or
   * is simply not in flight. It hands back `[]` only when the server actually
   * said "nothing". Every grid here used to receive `something || []` from its
   * parent, which threw that distinction away and let the table print "No data
   * available" — a factual claim — for a request that was never sent.
   *
   * So: normalise for the table, which needs an array either way, but REMEMBER
   * which it was. This makes the honest message the DEFAULT rather than
   * something each page has to opt into; a page-opt-in prop is how a capability
   * ends up missing on some screens (see CC-50013).
   */
  const readResolved = Array.isArray(data)
  const tableData = readResolved ? data : NO_ROWS

  const table = useReactTable({
    data: tableData,
    columns: allColumns,
    state: {
      sorting,
      columnVisibility,
      columnFilters,
      columnSizing,
      columnOrder: enableSelection
        ? ['_select', ...effectiveOrder]
        : effectiveOrder,
      ...(enableSelection ? { rowSelection } : {}),
    },
    enableColumnResizing: enableLayout,
    // CC-49333 / CC-49378: first click sorts ASCENDING for every column type.
    // Without this, TanStack's auto sort direction makes numeric/date columns
    // sort DESCENDING on first click (getAutoSortDir), which diverged from
    // ReportDataGrid (which already sets sortDescFirst per column) and the
    // dashboard tiles. Column defs can still override explicitly if ever needed.
    sortDescFirst: false,
    // CC-49331: shift-click adds a secondary (then tertiary) sort level.
    enableMultiSort: true,
    maxMultiSortColCount: 3,
    isMultiSortEvent: (e) => e.shiftKey,
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    ...(enableSelection ? {
      enableRowSelection: true,
      onRowSelectionChange: setRowSelection,
    } : {}),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // CC-50262 — OPT-IN, and the opt-in is the important part.
    //
    // TanStack defaults `autoResetPageIndex` to TRUE, so any new `data` identity
    // snaps the table back to page 1. The Report Queue polls every 5s with a
    // freshly-built array, so a user who paged to 2 was thrown back to 1 seconds
    // later without touching anything.
    //
    // Turning it off GLOBALLY looked like the tidy fix and is not safe: that
    // auto-reset also forces an extra render, and the report grids' totals strip
    // turns out to depend on it — `onVisibleRowsChange` publishes the filtered rows
    // in an effect, the parent recomputes totals from them, and without the extra
    // commit the footer renders one render behind. Trial Balance showed
    // "Totals reflect the 0 filtered rows shown (all 2)" with $0.00 debits and
    // credits. A financial grid displaying $0.00 totals, even for one frame, is
    // exactly what this app must never do — so the change is confined to grids
    // that ask for it. The underlying totals-strip ordering is a separate defect,
    // written up rather than fixed here.
    ...(keepPageOnDataChange ? { autoResetPageIndex: false } : {}),
    initialState: {
      pagination: { pageSize: resolvedPageSize },
    },
  })

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    // Bug #10: without a sortable coordinateGetter, Space lifted a column but
    // arrow keys couldn't move it — keyboard reorder was inert (WCAG 2.1.1).
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event
      if (!active || !over || active.id === over.id) return
      // CC-49462: the frozen column is not part of the reorder set — neither as
      // the thing dragged nor as a drop target, or it would end up away from the
      // edge it is pinned to.
      if (pinnedColumnId && (active.id === pinnedColumnId || over.id === pinnedColumnId)) return
      // spec 094 — after the early returns, so a drag that changed nothing is
      // not counted as a reorder. `detail` is a column ID, never a label: a
      // firm can rename a column (column aliases) and a label is firm data.
      trackGridTransform(storageKey, 'column_reorder', String(active.id))
      setColumnOrder((prev) => {
        // Reorder against the order the user can actually see (pin hoisted),
        // then store that — so the stored order and the rendered order agree.
        const cur = orderWithPinFirst(prev, pinnedColumnId)
        const oldIndex = cur.indexOf(active.id)
        const newIndex = cur.indexOf(over.id)
        if (oldIndex === -1 || newIndex === -1) return prev
        return arrayMove(cur, oldIndex, newIndex)
      })
    },
    [pinnedColumnId, storageKey]
  )

  const handleExport = useCallback(() => {
    const visibleColumns = table
      .getVisibleLeafColumns()
      .map((col) => col.columnDef)

    const rows = table.getFilteredRowModel().rows.map((row) => {
      const obj = {}
      visibleColumns.forEach((colDef) => {
        const key = colDef.accessorKey || colDef.id
        const header =
          typeof colDef.header === 'string' ? colDef.header : colDef.id
        obj[header] = colDef.accessorFn
          ? colDef.accessorFn(row.original)
          : row.original[key]
      })
      return obj
    })

    const csv = unparse(rows)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${exportFilename}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }, [table, exportFilename])

  // CC-49825: printing must include EVERY row, not just the current page.
  // Client-side pagination keeps only ~pageSize rows in the DOM, so Ctrl+P /
  // the Print button produced one page of data and blank pages after it (the
  // print CSS unlocks the scroll containers, but it can't reveal rows the
  // table never rendered). Same beforeprint/flushSync pattern as
  // ReportGroupedGrid's group pagination.
  const [printing, setPrinting] = useState(false)
  useEffect(() => {
    const on = () => flushSync(() => setPrinting(true))
    const off = () => setPrinting(false)
    window.addEventListener('beforeprint', on)
    window.addEventListener('afterprint', off)
    return () => {
      window.removeEventListener('beforeprint', on)
      window.removeEventListener('afterprint', off)
    }
  }, [])

  const headerGroups = table.getHeaderGroups()
  const rows = printing ? table.getPrePaginationRowModel().rows : table.getRowModel().rows
  const pageCount = table.getPageCount()
  const pageIndex = table.getState().pagination.pageIndex

  // The other half of CC-50262. With the auto-reset off, a list that SHRINKS could
  // strand the user on a page that no longer exists (dismiss the last two queue
  // jobs while on page 2 → a permanently empty table with no way back). Clamp to
  // the last real page instead — the narrow, deliberate version of the reset we
  // just removed: it fires on the page count changing, never on a refresh that
  // returns the same number of pages.
  useEffect(() => {
    if (!keepPageOnDataChange) return
    if (pageCount > 0 && pageIndex > pageCount - 1) table.setPageIndex(pageCount - 1)
  }, [keepPageOnDataChange, pageCount, pageIndex, table])

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
        <LoadingSpinner message={loadingMessage} />
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Bulk Action Bar */}
      {enableSelection && Object.keys(rowSelection).length > 0 && bulkActions && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-cb-accent/5 border-b border-cb-accent/20">
          <span className="text-sm font-medium text-cb-accent-text">
            {Object.keys(rowSelection).filter((k) => rowSelection[k]).length} selected
          </span>
          <button
            onClick={() => setRowSelection({})}
            className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
          >
            Clear
          </button>
          <div className="flex-1" />
          {bulkActions.map((action) => (
            <button
              key={action.label}
              onClick={() => {
                const selectedRows = Object.keys(rowSelection)
                  .filter((k) => rowSelection[k])
                  .map((idx) => tableData[parseInt(idx)])
                  .filter(Boolean)
                action.onClick(selectedRows)
              }}
              disabled={action.isLoading}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                action.variant === 'danger'
                  ? 'text-red-600 bg-red-50 hover:bg-red-100'
                  : 'text-cb-accent-text bg-cb-accent/10 hover:bg-cb-accent/20'
              } disabled:opacity-50`}
            >
              {action.isLoading ? 'Processing...' : action.label}
            </button>
          ))}
        </div>
      )}

      {/* Grid-owned query controls (Group by / Filter), on their OWN row above the
          record count — Jordan, 2026-08-26. They transform the RESULT, so they
          belong to this card rather than to the query card two cards up, and a
          full-width row is the only place with room for a long column name plus
          its value dropdown without the header wrapping. Renders nothing when the
          host passes none, which is every consumer except CsvReportGrid /
          AutoReportGrid. */}
      {headerRow && (
        <div className="border-b border-gray-100 px-4 py-2.5">{headerRow}</div>
      )}

      {/* Toolbar — "Bar 3" in the mock's language when reportChrome is on. */}
      <div className={`flex items-center justify-between border-b border-gray-100 ${reportChrome ? 'gap-3 px-4 py-2.5' : 'px-4 py-2'}`}>
        <div className={reportChrome ? 'flex min-w-0 items-baseline gap-3' : 'text-xs text-gray-500'}>
          {/* CC-53132: the count is a CLAIM. An unresolved read has counted
              nothing, so it renders no number at all rather than "0 rows" —
              and it used to read `data.length` straight, which threw on the
              `undefined` react-query hands back for a paused fetch. */}
          {readResolved && (
            <span className={reportChrome ? 'text-[13px] font-semibold text-rc-ink' : undefined}>
              {/* Spec 211: counts group their digits like every figure ("1,234 rows"). */}
              {table.getFilteredRowModel().rows.length === tableData.length
                ? `${tableData.length.toLocaleString('en-US')} ${tableData.length === 1 ? rowNoun : `${rowNoun}s`}`
                : `${table.getFilteredRowModel().rows.length.toLocaleString('en-US')} of ${tableData.length.toLocaleString('en-US')} ${rowNoun}s`}
            </span>
          )}
          {/* The mock's muted note beside the count ("Negative amounts are credit
              balances.") — a report explains its own numbers where they are read. */}
          {reportChrome && gridNote && (
            <span className="truncate text-[12.5px] text-rc-muted">{gridNote}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {enableExport && !reportChrome && (
            <button onClick={handleExport} className="btn-cb-toolbar">
              Export CSV
            </button>
          )}
          {/*
            spec 092 — REPORT CHROME: one "Display View" menu instead of three
            loose buttons plus a naked checkbox row above the grid.
            The designer's note beside this strip: "Everything that only changes
            the grid lives here, next to the grid. Nothing reloads, the changes
            are instant. Sections are labelled so users can see the grouping
            rather than having to work it out."
            So: COLUMNS (show/hide + alignment), VIEW OPTIONS (whatever the report
            grid contributes — show-$, hide-$0, hide-inactive), and the layout
            actions last because "Reset grid layout" is destructive-ish.
            Non-report pages keep the three separate buttons exactly as before.
          */}
          {reportChrome ? (
            <>
              {/* Host-owned strip buttons (Rename columns) sit to the LEFT of
                  Display View — column controls read as one cluster.
                  Deliberately OUTSIDE the segmented wrapper below:
                  stripActionsPlacement.test.jsx requires Rename columns to be the
                  button immediately before Display View, and welding a
                  host-supplied button of unknown geometry into the pair would
                  make the seam depend on what the host passed. */}
              {stripActions}
              {/*
                E4 (2026-08-27) — Display View + Column Filter are ONE segmented
                control. Himaad's review: the standalone Column Filter button read
                as orphaned; it changes what the grid shows, so it belongs with
                the grid-display cluster rather than floating beside it.

                A wrapper, not a merge. They stay two buttons with two accessible
                names because they are two capabilities — and because
                e2e/reports-grid-audit.spec.js:567-587 measures each one BY NAME
                and hard-asserts 38px, so a single merged control would fail as a
                missing control (null), not as a wrong height.

                `inline-flex` and nothing else: the strip's own `gap-2` would
                otherwise put 8px between the halves. The seam and the corners are
                .btn-rc-seg-l / .btn-rc-seg-r in src/index.css — NOT
                `rounded-r-none` here, which src/index.css being unlayered would
                silently discard (see that file's .btn-cb-split-* note).

                No <button> is added or reordered, so the two placement gates
                still hold: `displayIdx - renameIdx === 1` reads document order,
                which a wrapper element does not change.
              */}
              <span className="inline-flex items-center">
              <PopoverMenu
                label="Display view options"
                triggerLabel="Display View"
                width={288}
                /* Segmented only when the right half actually renders. With
                   `enableFiltering={false}` Display View is alone on the strip,
                   and a flattened right edge with nothing attached to it reads as
                   a clipped control. */
                triggerClassName={enableFiltering ? 'btn-rc btn-rc-seg-l' : 'btn-rc'}
                triggerContent={<><SlidersIcon className="h-3.5 w-3.5" /> Display View</>}
                data-pendo="report-display-view"
              >
                {/* Himaad UI feedback A2 (2026-08-26) — panel breathing room, and an
                    internal inconsistency this menu already had before he flagged it:
                    the COLUMN rows below were py-[5px] while the ACTION rows in the
                    same menu (Freeze / "Reset grid layout") were py-[7px]. Both are py-[7px]
                    now, with gap-2.5 matching the button's own inner gap. Horizontal
                    padding is deliberately unchanged — the rows are already wide
                    enough and px-3.5 is what every other row in the menu uses.

                    The clamp moves WITH the rows: at 5px this box showed ~8 columns,
                    and raising the rows without raising the clamp would have shown ~6
                    and read MORE cramped than before the fix. 220 -> 264 keeps ~8
                    visible, so the extra room is felt rather than paid for.

                    Section headings: this one had pt-0.5 and "View options" below had
                    no pt at all; both are pt-1. */}
                <p className="px-3.5 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-rc-muted">Columns</p>
                <div className="max-h-[264px] overflow-y-auto">
                  {table.getAllLeafColumns().map((column) => {
                    const label = columnPickerLabel(column)
                    // A row, not one big button: the alignment select is itself
                    // interactive, and nesting a <select> inside a <button> is
                    // invalid HTML. The toggle keeps the menuitemcheckbox role so
                    // PopoverMenu's roving focus still finds it; the select is a
                    // sibling.
                    return (
                      <div
                        key={column.id}
                        className="flex w-full items-center gap-2.5 px-3.5 py-[7px] text-[13px] text-rc-ink-2 hover:bg-gray-50"
                      >
                        <button
                          type="button"
                          role="menuitemcheckbox"
                          aria-checked={column.getIsVisible()}
                          data-pendo="report-display-view-column"
                          onClick={() => {
                            // spec 094 — the discrete user action. The store
                            // write happens in an effect that also fires on
                            // mount and on a view apply, so wrapping that
                            // instead would count things nobody clicked.
                            trackGridTransform(storageKey, 'column_hide', String(column.id))
                            column.toggleVisibility(!column.getIsVisible())
                          }}
                          className="flex min-w-0 flex-1 items-center gap-2.5 text-left focus-visible:outline-none focus-visible:underline"
                        >
                          <span
                            aria-hidden="true"
                            /* border-rc-seam, NOT border-rc-control — spec 117 §5 G1,
                               closed 2026-09-09 (spec 121 gap 3). This row is
                               `hover:bg-gray-50` (#f9fafb), where #8b95a5 is 2.896:1
                               and under SC 1.4.11's 3.0:1 exactly while a pointer or
                               keyboard focus is on it; #6b7280 is 4.626:1. It is the
                               same one-token fix ControlCheckbox took, and until now
                               this popover showed its column rows one step lighter
                               than the View options rows eight pixels below them —
                               which is literally Himaad's B4 complaint.
                               Still an inline copy of the box rather than
                               <ControlCheckbox>: this lives inside a
                               <button role="menuitemcheckbox"> that PopoverMenu roves
                               with Arrow keys, so it cannot hold an <input> without
                               breaking the rove (spec 117 §5 G6). Collapsing the two
                               remaining inline copies onto a presentational variant
                               needs PopoverMenu's ITEM_SELECTOR widened first. */
                            className={`grid h-3.5 w-3.5 flex-none place-items-center rounded-[3px] border ${column.getIsVisible() ? 'border-rc-chip-text bg-rc-chip-text text-white' : 'border-rc-seam bg-white'}`}
                          >
                            {column.getIsVisible() && (
                              <svg viewBox="0 0 10 8" className="h-2 w-2.5" fill="none" stroke="currentColor" strokeWidth={2.2}>
                                <path d="M1 4l2.5 2.5L9 1" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </span>
                          <span className="min-w-0 flex-1 truncate">{label}</span>
                        </button>
                        {/* Per-column alignment override (CC-49735). It lived in the
                            old Columns dropdown, so it has to come across with the
                            rest or report grids silently lose the capability.

                            Himaad A2, "the auto dropdown is cramped" — this is as far
                            as that CAN be taken: Auto is an <option> inside a NATIVE
                            select, and the opened list is drawn by the OS and is not
                            stylable from here. So the fix is the closed control: px-1
                            py-0.5 -> px-1.5 py-1, and `rounded` -> rounded-rc-control
                            (B2's 6px).

                            The border matters more than the padding. It was
                            border-rc-line — 1.24:1, the token index.css marks
                            DECORATIVE ONLY — and this was the single interactive
                            element in the tree wearing it. border-rc-control is
                            3.03:1. It stays a native select on purpose: a select is
                            keyboard-operable under PopoverMenu's roving focus and a
                            popover replacement is not; that swap was tried and
                            reverted (see src/index.css, .select-rc block). */}
                        {enableAlignment && (
                          <select
                            aria-label={`Alignment for ${label}`}
                            data-pendo="report-column-align"
                            value={columnAlignment?.[column.id] || ''}
                            onChange={(e) => {
                              trackGridTransform(storageKey, 'align', e.target.value || 'auto')
                              onColumnAlignmentChange?.(column.id, e.target.value || null)
                            }}
                            className="flex-none rounded-rc-control border border-rc-control px-1.5 py-1 text-[11px] text-rc-muted focus:border-rc-chip-line focus:ring-2 focus:ring-rc-chip-line/20"
                          >
                            <option value="">Auto</option>
                            <option value="left">Left</option>
                            <option value="center">Center</option>
                            <option value="right">Right</option>
                          </select>
                        )}
                      </div>
                    )
                  })}
                </div>
                {/* The report grid's own display toggles. These were a naked
                    checkbox row floating above the grid — the scruffiest part of
                    the old page and the clearest thing this panel is for. */}
                {displayViewExtras && (
                  <>
                    <div className="my-1.5 h-px bg-rc-line" />
                    <p className="px-3.5 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-rc-muted">View options</p>
                    <div className="px-3.5 pb-1 text-[13px] text-rc-ink-2">{displayViewExtras}</div>
                  </>
                )}
                {((enablePinning && storageKey) || (enableLayout && storageKey)) && (
                  <>
                    <div className="my-1.5 h-px bg-rc-line" />
                    {enablePinning && storageKey && (
                      <button
                        type="button"
                        role="menuitemcheckbox"
                        aria-checked={!!pinnedColumnId}
                        data-pendo="report-freeze-first-column"
                        onClick={handleTogglePin}
                        disabled={!pinnedColumnId && !pinTargetId}
                        className="flex w-full items-center px-3.5 py-[7px] text-left text-[13px] text-rc-ink-2 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none disabled:opacity-40"
                        title="Keep the first column visible while you scroll a wide report sideways"
                      >
                        {pinnedColumnId ? 'Unfreeze first column' : 'Freeze first column'}
                      </button>
                    )}
                    {enableLayout && storageKey && (
                      <button
                        type="button"
                        role="menuitem"
                        /* E4: the VALUE is deliberately unchanged across the
                           rename. Pendo has been collecting on QA since
                           2026-08-26; re-keying this selector would fork the
                           metric into two series that look like a usage cliff. */
                        data-pendo="report-reset-layout"
                        onClick={handleResetLayout}
                        className="flex w-full items-center px-3.5 py-[7px] text-left text-[13px] text-rc-ink-2 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
                        /*
                          E4 verify pass (2026-08-28) — this promised more than
                          it delivers, twice, and both halves are fixed here.

                          1. "and view options" was unconditional. A host that
                             contributes none (every non-report list page) has no
                             view options to reset, and until this batch a host
                             that contributed some via `pageDisplayExtras` alone
                             kept them. The clause is now gated on the seam that
                             actually does the work.
                          2. "to the report's default" was simply wrong. All four
                             view options initialise from `initialToggles` — a
                             saved view or a reopened job — so a view seeding
                             hideZero:true has THAT as its default for that
                             mount, and the reset overrides it with the built-in
                             value regardless. Saying "the built-in defaults —
                             not to a saved view's" is the true statement, and
                             the divergence itself is deliberate: see
                             gridDisplayDefaults.js for why it must stay
                             row-revealing.

                          "its own": a page's `pageDisplayExtras` controls are
                          NOT covered by this clause. When the page supplies no
                          reset hook, ReportDataGrid renders a caveat saying so
                          inside this very menu — this grid cannot see that
                          configuration, and a tooltip is the wrong place to
                          report it anyway.
                        */
                        title={`Reset this grid's columns, widths, sort, column filters${onResetDisplayOptions ? ' and its own view options' : ''} to the built-in defaults — not to a saved view's. Does not change the report's filters or re-run it.`}
                      >
                        {/*
                          E4 (2026-08-27) — "Reset layout" -> "Reset grid layout".
                          There are now two resets on a report page and each has
                          to say its own scope: "Reset filters" in the query card
                          resets filter VALUES and re-runs; this one resets LAYOUT
                          and touches no figure the server sent. The old pair,
                          "Reset" and "Reset layout", left the difference to be
                          inferred.

                          The non-report-chrome copy further down keeps "Reset
                          layout": the nine list pages that render it have no
                          second reset to be confused with, and DataGrid.test.jsx
                          exercises that branch by its exact text.
                        */}
                        Reset grid layout
                      </button>
                    )}
                  </>
                )}
              </PopoverMenu>

              {/* One button, one behaviour, both paths. The per-column filter row
                  used to be permanently on for flat reports and toggleable on
                  grouped ones; it is the same control on both now. It starts
                  CLOSED because the mock's grid has no filter row — the capability
                  is one labelled click away rather than a permanent row of unnamed
                  inputs, which is also more discoverable than it was.

                  B3 (2026-08-26): `h-[30px] text-[13px]` is GONE from here and from
                  the Display View trigger above. It was never applied — src/index.css
                  carries no @layer, Tailwind emits utilities inside `@layer utilities`,
                  and an unlayered rule beats a layered one whatever the specificity, so
                  .btn-rc's own 2.375rem/0.875rem always won. Both controls have been
                  38px/14px all along; the utilities only implied a second height
                  existed. Deleting them is a no-op on screen and stops the next reader
                  from "fixing" a mismatch that isn't there. */}
              {enableFiltering && (
                <button
                  type="button"
                  data-pendo="report-column-filter"
                  onClick={() => setFilterRowOpen((v) => !v)}
                  /* Both, matching ReportGroupedGrid.jsx:1433-1434. aria-pressed
                     is what src/test/gridChrome.js and the e2e spec's idempotent
                     open guard read; aria-expanded is what the row below it
                     actually is, and a disclosure owes it. Both track the DERIVED
                     visibility, so neither can ever claim "closed" over an open
                     row (which is the state a seeded filter puts this in). */
                  aria-pressed={filterRowVisible}
                  aria-expanded={filterRowVisible}
                  className={`btn-rc btn-rc-seg-r ${filterRowVisible ? 'is-active' : ''}`}
                  title="Filter the rows on screen by a column's values"
                >
                  <FunnelIcon className="h-3.5 w-3.5" /> Column Filter{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                </button>
              )}
              </span>
            </>
          ) : (
            <>
              {/* CC-49462: freeze/unfreeze the identity column. A real <button> with
                  aria-pressed, so it is keyboard-operable without extra handlers. */}
              {enablePinning && storageKey && (
                <button
                  type="button"
                  onClick={handleTogglePin}
                  aria-pressed={!!pinnedColumnId}
                  // Nothing visible to freeze (every column hidden) — disabled
                  // rather than inert, so the control still describes reality.
                  disabled={!pinnedColumnId && !pinTargetId}
                  className={`btn-cb-toolbar ${pinnedColumnId ? 'is-active' : ''}`}
                  title="Keep the first column visible while you scroll a wide report sideways"
                >
                  {pinnedColumnId ? 'Unfreeze first column' : 'Freeze first column'}
                </button>
              )}
              {enableLayout && storageKey && (
                <button
                  onClick={handleResetLayout}
                  className="btn-cb-toolbar"
                  /* E4 verify pass (2026-08-28): "this report's default" was
                     inaccurate here twice over — these are the built-in
                     defaults, and this branch renders on the nine non-report
                     list pages, where there is no report. The LABEL stays
                     "Reset layout": these pages have no second reset to be
                     confused with, and DataGrid.test.jsx pins that exact text. */
                  title="Reset columns, sizes, sort and filters to the built-in defaults"
                >
                  Reset layout
                </button>
              )}
              <ColumnVisibilityDropdown
                table={table}
                enableAlignment={enableAlignment}
                columnAlignment={columnAlignment || {}}
                onColumnAlignmentChange={onColumnAlignmentChange}
              />
            </>
          )}
        </div>
      </div>

      {/* Table. CC-49825 (QA rejection): the fixed-height scroll container
          CLIPS on paper — the browser prints its scrolled box, so page 1 had
          data and every later page was blank even with all rows in the DOM.
          print:* lifts the clamp (and the sticky header goes static so it
          can't repeat mid-page over rows). */}
      <div ref={enablePinning ? scrollRef : undefined} className={stickyHeader ? 'overflow-auto max-h-[calc(100vh-15rem)] print:overflow-visible print:max-h-none' : 'overflow-x-auto print:overflow-visible'}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          {/* table-fixed (report grids only, via enableLayout) so dragged column
              widths are honored and long cells wrap within them instead of the
              browser redistributing widths and flooring the column (bug #5).

              role="table" + aria-label (CC-50173): the repo's ADA rule asks for the
              explicit role, and an accessible NAME is the half that actually changes
              what a screen-reader user hears — a page with a grid plus a totals
              footer plus a filter row otherwise announces an unnamed "table". */}
          <table
            role="table"
            aria-label={tableLabel}
            className={`w-full ${enableLayout ? 'table-fixed' : ''}`}
          >
            <thead className={`border-b border-gray-100 ${stickyHeader ? 'bg-gray-50 sticky top-0 z-20 print:static' : 'bg-gray-50/80'}`}>
              {headerGroups.map((headerGroup) => (
                <tr key={headerGroup.id}>
                  <SortableContext
                    items={effectiveOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header, headerIdx) => {
                      const sortDir = header.column.getIsSorted()
                      const headerLabel = typeof header.column.columnDef.header === 'string'
                        ? header.column.columnDef.header
                        : header.column.id
                      return (
                        <DraggableHeader
                          key={header.id}
                          header={header}
                          normalCase={normalCaseHeaders}
                          isPinned={!!pinnedColumnId && header.column.id === pinnedColumnId}
                          isLastHeader={headerIdx === headerGroup.headers.length - 1}
                          ariaSortValue={sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : undefined}
                        >
                          {header.isPlaceholder ? null : (
                            <span className="inline-flex items-center gap-1">
                              {header.column.getCanSort() ? (
                                <button
                                  type="button"
                                  className="cursor-pointer select-none flex items-center gap-1 bg-transparent border-none p-0"
                                  onClick={header.column.getToggleSortingHandler()}
                                  aria-label={`Sort by ${headerLabel}`}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                  {/* Himaad UI feedback B5 (2026-08-26): the sort mark
                                      joins the design system's minimal thin-grey
                                      language, which already exists ONE SPAN OVER in
                                      this same th — the frozen marker is text-gray-400
                                      text-[10px] leading-none and the drag handle is
                                      text-gray-300 text-[10px] leading-none, while this
                                      was a 12-13px #064891 mark shouting over both.
                                      text-rc-muted (#6b7280) is 4.83:1 on white and
                                      4.63:1 on the gray-50 header, so it still clears
                                      4.5:1 as small TEXT as well as the 3:1 non-text
                                      floor — this is the sole indicator of sort state
                                      besides aria-sort, so it may not go decorative.
                                      The CHARACTERS stay literal: six test files assert
                                      textContent contains them, and ReportGroupedGrid
                                      fuses a superscript level index into the same
                                      string. Still rendered only when a sort is
                                      active. */}
                                  {sortDir === 'asc' && (
                                    <span className="text-rc-muted text-[10px] leading-none">▲</span>
                                  )}
                                  {sortDir === 'desc' && (
                                    <span className="text-rc-muted text-[10px] leading-none">▼</span>
                                  )}
                                </button>
                              ) : (
                                <span>
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                                </span>
                              )}
                              {/* D3 (2026-08-27): a definition that is only the
                                  column's type plus the generic sort/filter hint
                                  earns no icon — see columnDefinitionGate.js. The
                                  SAME predicate gates the grouped grid, so these
                                  two cannot drift apart. */}
                              {isAuthoredDefinition(header.column.columnDef.meta?.definition) ? (
                                <span
                                  role="img"
                                  aria-label={`About ${headerLabel}: ${header.column.columnDef.meta.definition}`}
                                  title={header.column.columnDef.meta.definition}
                                  className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-gray-400 text-[9px] leading-none text-gray-500 cursor-help"
                                >
                                  i
                                </span>
                              ) : null}
                            </span>
                          )}
                        </DraggableHeader>
                      )
                    })}
                  </SortableContext>
                </tr>
              ))}
              {enableFiltering && (!reportChrome || filterRowVisible) && (
                <tr className="border-b border-gray-100">
                  {headerGroups[headerGroups.length - 1]?.headers.map((header) => (
                    <th
                      key={header.id}
                      scope="col"
                      /* A3 round 2 — the third of Himaad's three specifics, the one
                         round 1 missed: "the placement relative to the label text
                         above it. In the mock the field is visually separated; in
                         the build it reads as one merged field."
                         py-1.5 put 6px above the input, and the header row's own
                         py-3 sat directly on top of it inside the SAME grey thead
                         band with no rule between — measured 23px from the label's
                         baseline box to the input. pt-3 pb-2.5 opens that to ~29px
                         so the field reads as its own row rather than as more of
                         the header. Keep this in step with ReportGroupedGrid's
                         filter row or 86 grouped reports drift from the 33 flat. */
                      className={`px-4 pt-3 pb-2.5 ${!!pinnedColumnId && header.column.id === pinnedColumnId ? PIN_CELL_CLASS : ''}`}
                    >
                      <ColumnFilterCell header={header} />
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            {/* Himaad UI feedback B4 (2026-08-26) — row separators, and the hover
                that was cancelling them out.

                Measured before: the divider was divide-gray-50 (#f9fafb, 1.05:1 on
                white — under a pixel of perceived edge) and the row hover was
                bg-gray-50/50, which composites to #fcfdfd. Divider against hovered
                row: 1.03:1. So pointing at a row ERASED that row's own separators,
                which is exactly backwards — the moment you are reading across a row
                is the moment you need its boundaries.

                Now: the divider is --color-rc-line (#e5e7eb, 1.24:1 on white, 1.18:1
                on the hovered row) and the hover is the full bg-gray-50 (#f9fafb).
                The point is the ORDERING, not the ratio — the divider is now strictly
                DARKER than the hover fill, so no hover state can ever cancel it, where
                before the two were the same colour by construction. That is also the
                grouped grid's hover exactly (ReportGroupedGrid.jsx `hover:bg-gray-50`),
                so the two grids stop disagreeing about what a hovered row looks like.

                Deliberately NOT darkened past rc-line: a row rule identifies nothing
                and is decorative, so the 3:1 control floor does not apply and a heavier
                rule would turn a 500-row financial grid into graph paper. The
                controls in this file are where the 3:1 work went (see the resize
                handle and the ADA sweep above). */}
            <tbody className="divide-y divide-rc-line">
              {rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={table.getVisibleLeafColumns().length}
                    className="px-4 py-12 text-center text-sm text-gray-500"
                  >
                    {/* CC-53132: only a RESOLVED read may be described as empty.
                        An unresolved one gets `role="alert"` because the user is
                        looking at a table that appears to say "nothing here" and
                        needs to be told otherwise. */}
                    {readResolved ? emptyMessage : <span role="alert">{unresolvedMessage}</span>}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={row.id}
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    /* Opt-in per grid (spec 111). A grid whose rows carry rich,
                       state-dependent cells needs a stable per-row handle for its
                       tests; `getByRole('row')` indexes positionally, which breaks
                       the moment sorting or a new column changes the order and
                       gives a failure that names the wrong cause. Absent unless a
                       caller asks, so no existing grid's DOM changes. */
                    data-testid={rowTestId}
                    className={`hover:bg-gray-50 transition-colors ${
                      onRowClick ? 'cursor-pointer' : ''
                    }`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        // CC-49460: wrap long values instead of clipping/overflowing.
                        // break-words lets long unbroken tokens (matter numbers, emails,
                        // URLs) wrap within the column's resized width; align-top keeps
                        // multi-line cells readable against single-line neighbors.
                        className={`${PENDO_IGNORE} px-4 py-3 text-sm text-gray-700 break-words align-top ${ALIGN_TEXT[cell.column.columnDef.meta?.align] || ''} ${!!pinnedColumnId && cell.column.id === pinnedColumnId ? PIN_CELL_CLASS : ''}`}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
            {/* Totals footer (CC-49735): a real table row so each total sits
                directly under its column, honoring order, visibility, widths,
                and alignment. Sticky within the scroll region like the grouped
                grid's tfoot. */}
            {footerCells && Object.keys(footerCells).length > 0 && (
              <tfoot className="bg-gray-100 font-semibold sticky bottom-0 z-10 print:static">
                <tr>
                  {table.getVisibleLeafColumns().map((col, idx) => (
                    <td
                      key={col.id}
                      className={`${PENDO_IGNORE} px-4 py-2 text-sm text-gray-900 border-t border-gray-200 break-words align-top ${ALIGN_TEXT[col.columnDef.meta?.align] || ''} ${!!pinnedColumnId && col.id === pinnedColumnId ? PIN_CELL_CLASS : ''}`}
                      style={{ width: col.getSize() }}
                    >
                      {idx === 0 && (
                        <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                          Totals
                        </span>
                      )}
                      {footerCells[col.id] ?? null}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </DndContext>
      </div>

      {/* Pagination (hidden in print — CC-49825 prints every row instead).
          2026-08-26 ADA sweep: both pager buttons were white-on-white with a
          border-gray-200 (1.24:1) outline — the only thing identifying them as
          controls, so SC 1.4.11's 3:1 applies. border-rc-control is 3.03:1.
          rounded-lg stays: B2 normalises the --radius-cb consumers, and these were
          never one of them. */}
      {pageCount > 1 && (
        <div data-print-hide className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <div className="text-xs text-gray-500">
            Page {pageIndex + 1} of {pageCount}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="px-3 py-1 text-xs font-medium rounded-lg border border-rc-control text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="px-3 py-1 text-xs font-medium rounded-lg border border-rc-control text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DataGrid
